import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Loader } from '@onramp/components/Loader';
import { PageWrapper } from '@onramp/components/PageWrapper';
import { genericMessages } from '@onramp/utils/genericMessages';
import type { RouteKey } from '@onramp/utils/routes';
import { getRouteKeyFromPath, Routes } from '@onramp/utils/routes';
import { styled } from 'linaria/react';
import { VStack } from '@cbhq/cds-web/layout';
import { TextLabel1 } from '@cbhq/cds-web/typography';
import { SplashScreenContext } from './context';
const routeKeyAllowList = [Routes.Buy.OneClick, Routes.Buy.SelectAsset, Routes.Buy.Return3DS].map(getRouteKeyFromPath).filter((routeKey): routeKey is RouteKey => !!routeKey);
export function OneClickBuyLoader() {
  const {
    formatMessage
  } = useIntl();
  const [loadingMessage, setLoadingMessage] = useState(formatMessage(genericMessages.loadingPaymentMethod)); // [1]
  const headerText = formatMessage(genericMessages.coinbaseOnramp);
  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingMessage(formatMessage(genericMessages.fetchingQuote));
    }, 2000);
    return () => clearInterval(interval);
  }, [formatMessage]);
  return <PageWrapper headerText={headerText} hideHamburgerMenu>
      <VStack gap={3}>
        <Loader />
        <TextLabel1 as="span">{loadingMessage}</TextLabel1>
      </VStack>
    </PageWrapper>;
}

/**
 * This splash screen renders a {@link PageWrapper} with a {@link Loader} in it over the top of any other
 * {@link PageWrapper} component. It provides a context that contains a function that can be used to dismiss the splash
 * screen one the UI is ready. The splash screen is automatically dismissed after {@linkcode timeoutSeconds} seconds in
 * case it is rendered over a page that does not dismiss it correctly.
 * @param children the children to render
 * @param timeoutSeconds the number of seconds to wait before automatically dismissing the splash screen
 */
export default function SplashScreen({
  children,
  timeoutSeconds = 8
}: {
  children: ReactNode;
  timeoutSeconds?: number;
}) {
  const routeKey = getRouteKeyFromPath(window.location.href);
  const isAllowed = routeKey ? routeKeyAllowList.includes(routeKey) : false;
  const [isVisible, setIsVisible] = useState(isAllowed);
  const dismissSplashScreen = useCallback(() => setIsVisible(false), [setIsVisible]);
  const loadingScreenContext = useMemo(() => ({
    isVisible,
    dismissSplashScreen
  }), [isVisible, dismissSplashScreen]);
  const [seconds, setSeconds] = useState(timeoutSeconds);
  useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 1) {
        setSeconds(seconds - 1);
      }
      if (seconds === 1) {
        clearInterval(interval);
        void dismissSplashScreen();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [dismissSplashScreen, seconds]);
  const {
    formatMessage
  } = useIntl();
  const headerText = formatMessage(genericMessages.coinbaseOnramp);
  return <SplashScreenContext.Provider value={loadingScreenContext}>
      {isVisible && <LoadingWrapper>
          <PageWrapper headerText={headerText} hideHamburgerMenu>
            <Loader />
          </PageWrapper>
        </LoadingWrapper>}
      {children}
    </SplashScreenContext.Provider>;
}
const LoadingWrapper = /*#__PURE__*/styled("div")({
  name: "LoadingWrapper",
  class: "lssr5xx"
});

require("./SplashScreen.linaria.module.css!=!../../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./SplashScreen.tsx");